import { useEffect, useState } from "react";
import { Plus, Trash2, ArrowUp, ArrowDown, Building2 } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

export default function DealersTab() {
  const qc = useQueryClient();
  const [items, setItems] = useState(null);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [logo, setLogo] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/dealers").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
  }, []);

  if (!items) return <p className="font-mono text-xs uppercase tracking-[0.25em] text-subtle">Loading…</p>;

  const handleLogo = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    const { data } = await api.post("/admin/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
    setLogo(data.url);
    setUploading(false);
  };

  const reset = () => {
    setName("");
    setAddress("");
    setPhone("");
    setLogo("");
  };

  const add = async () => {
    if (!logo) return toast.error("Please upload a logo.");
    await api.post("/admin/dealers", { name, address, phone, logo });
    reset();
    toast.success("Dealer added.");
    load();
    qc.invalidateQueries({ queryKey: ["site"] });
  };

  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    setItems(next);
    await api.post("/admin/reorder", { collection: "dealers", ids: next.map((d) => d.id) });
    qc.invalidateQueries({ queryKey: ["site"] });
  };

  const del = async (d) => {
    if (!window.confirm(`Remove this dealer?`)) return;
    await api.delete(`/admin/dealers/${d.id}`);
    load();
    qc.invalidateQueries({ queryKey: ["site"] });
  };

  return (
    <div className="max-w-3xl">
      <div className="mb-8 rounded-3xl border border-line bg-white p-5 shadow-[0_14px_35px_-24px_rgba(13,13,17,0.18)]">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-mute">Add a dealer logo</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Dealer name (optional, internal only)" className="border border-line bg-white px-3.5 py-2.5 text-sm outline-none focus:border-srs sm:col-span-2" />
          <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone (optional)" className="border border-line bg-white px-3.5 py-2.5 text-sm outline-none focus:border-srs" />
          <input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Address (optional)" className="border border-line bg-white px-3.5 py-2.5 text-sm outline-none focus:border-srs" />
          <div className="flex items-center gap-3 sm:col-span-2">
            <input type="file" accept="image/*" onChange={handleLogo} className="text-sm" />
            {uploading && <span className="text-xs text-subtle">Uploading…</span>}
            {logo && <img src={logo} alt="" className="h-10 w-10 rounded border border-line object-contain" />}
          </div>
        </div>
        <button onClick={add} className="mt-4 flex items-center justify-center gap-2 bg-ink px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] text-white hover:bg-srs">
          <Plus size={14} /> Add dealer
        </button>
      </div>

      <div className="divide-y divide-line border border-line bg-white">
        {items.map((d, i) => (
          <div key={d.id} className="flex items-center gap-4 p-4">
            <span className="w-7 font-mono text-[11px] text-subtle">{String(i + 1).padStart(2, "0")}</span>
            {d.logo ? (
              <img src={d.logo} alt="" className="h-12 w-12 border border-line object-contain p-1" />
            ) : (
              <div className="flex h-12 w-12 items-center justify-center border border-line bg-mist text-mute"><Building2 size={18} /></div>
            )}
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-ink">{d.name || `Dealer ${i + 1}`}</p>
              {(d.address || d.phone) && <p className="truncate font-mono text-[10px] text-subtle">{d.address} {d.phone && `· ${d.phone}`}</p>}
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="p-2 text-mute hover:text-ink disabled:opacity-25"><ArrowUp size={15} /></button>
              <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="p-2 text-mute hover:text-ink disabled:opacity-25"><ArrowDown size={15} /></button>
              <button onClick={() => del(d)} className="p-2 text-mute hover:text-srs"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
        {!items.length && <p className="p-8 text-center text-sm text-subtle">No dealers added yet.</p>}
      </div>
    </div>
  );
}
